import type { FC, MouseEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { BlackEnvelopeTopicModal } from "components";

import { processApiError } from "utils/processApiError";

import type { BlindSpotThemeFragment } from "api/generated";
import { useGetBlindSpotThemesQuery, useRemoveThemeFromBlackEnvelopeMutation } from "api/generated";

const DEFAULT_SHOWN_TOPIC_COUNT = 5;

const BlackEnvelope: FC = () => {
  const { t } = useTranslation(["envelope"]);
  const navigate = useNavigate();

  const [removeEnvelope, { error: apiMutationError }] = useRemoveThemeFromBlackEnvelopeMutation();

  const { data } = useGetBlindSpotThemesQuery();

  const [themes, setThemes] = useState<BlindSpotThemeFragment[]>([]);
  const [shownTopicsCount, setShownTopicsCount] = useState(DEFAULT_SHOWN_TOPIC_COUNT);
  const [activeThemeModal, setActiveThemeModal] = useState<BlindSpotThemeFragment | null>(null);

  const mutationLoading = useRef(false);

  useEffect(() => {
    if (apiMutationError === undefined) return;

    processApiError(apiMutationError);
  }, [apiMutationError]);

  useEffect(() => {
    if (!data) return;

    setThemes(data.blindSpotThemes);
  }, [data]);

  const handleShowAll = () => {
    setShownTopicsCount(themes.length);
  };

  const handleClick = (e: MouseEvent<HTMLButtonElement>, theme: BlindSpotThemeFragment) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveThemeModal(theme);
  };

  const handleModalClose = () => {
    setActiveThemeModal(null);
  };

  const handleModalConfirm = (themeId: string) => {
    if (mutationLoading.current) return;
    mutationLoading.current = true;

    removeEnvelope({ id: themeId })
      .then(() => {
        setActiveThemeModal(null);
        setThemes(currentThemes => {
          const copy = [...currentThemes].filter(currentTheme => currentTheme.id !== themeId);

          return copy;
        });
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        mutationLoading.current = false;
      });
  };

  const handleShowExercises = (themeId: string) => {
    navigate(`/ulohy/${themeId}`);
  };

  return (
    <div>
      <p>{t("blackEnvelope.summary2", { ns: "envelope" })}</p>
      <div className={"tags"}>
        {themes.slice(0, shownTopicsCount).map(theme => (
          <button
            key={theme.id}
            className={"tag"}
            onClick={e => {
              handleClick(e, theme);
            }}
          >
            {theme.theme?.appName ?? theme.theme?.name ?? ""}
          </button>
        ))}
        {themes.length > shownTopicsCount ? (
          <a className={"tag"} onClick={handleShowAll} role="button">
            +{themes.length - shownTopicsCount}
          </a>
        ) : null}
      </div>
      {activeThemeModal === null ? null : (
        <BlackEnvelopeTopicModal
          onClose={handleModalClose}
          onConfirm={() => {
            handleModalConfirm(activeThemeModal.id);
          }}
          onShowExercises={() => {
            handleShowExercises(activeThemeModal.id);
          }}
          name={activeThemeModal.theme?.name ?? activeThemeModal.theme?.name ?? ""}
        />
      )}
    </div>
  );
};

export default BlackEnvelope;
